import * as t from 'io-ts';

import {PublicId, tryParsePublicId} from '../applications/publicIdTypes';
import {makeApiType} from './apiTypes';

/*
 * Runtime type.
 */

const RCircle = t.readonly(
  t.intersection([
    t.type({
      id: t.Integer,
      name: t.string,
      namespace: t.string,
      teammateIds: t.readonlyArray(t.Integer),
      url: t.string,
      createdAt: t.number,
    }),
    t.partial({
      description: t.string,
    }),
  ]),
);
const RCircles = t.readonlyArray(RCircle);

const RCircleRelationships = t.type({
  teammates: t.readonlyArray(t.type({teammateId: t.Integer, addedAt: t.number})),
});

/*
 * Type.
 */

export interface CircleModel extends t.TypeOf<typeof RCircle> {}
export interface UnsavedCircleModel extends Omit<CircleModel, 'id' | 'url' | 'createdAt'> {}
export interface CircleRelationshipsModel extends t.TypeOf<typeof RCircleRelationships> {}

export type CirclePublicId = PublicId<'circle'>;

/*
 * IO methods.
 */

function parseCircle(src: any): CircleModel {
  return {
    id: src.id,
    name: src.name,
    description: src.description,
    namespace: src.namespace,
    teammateIds: src.teammate_ids,
    url: src.url,
    createdAt: src.created_at,
  };
}

function parseCircles(src: any): readonly CircleModel[] {
  return src.circles?.map(parseCircle);
}

export function serializeCircle(src: UnsavedCircleModel) {
  return {
    name: src.name,
    description: src.description,
    teammate_ids: src.teammateIds,
    namespace: src.namespace,
  };
}

function parceCircleTeammateRelationship(src: any): CircleRelationshipsModel['teammates'][number] {
  return {
    teammateId: src.teammate_id,
    addedAt: src.added_at,
  };
}

function parceCircleRelationships(src: any): CircleRelationshipsModel {
  return {
    teammates: (src.teammates ?? []).map(parceCircleTeammateRelationship),
  };
}

export function isCirclePublicId(src: unknown): src is CirclePublicId {
  return typeof src === 'string' && tryParsePublicId(src, 'circle') !== undefined;
}

/*
 * API type.
 */

export const ApiCircle = makeApiType('circle', RCircle, parseCircle);
export const ApiCircles = makeApiType('circles', RCircles, parseCircles);
export const ApiCircleRelationships = makeApiType(
  'circleRelationships',
  RCircleRelationships,
  parceCircleRelationships,
);
